import { j as jsxRuntimeExports } from "./auth-service-tools177.js";
import dayjsImport from "./auth-service-tools36.js";
import { useState, useEffect } from "react";
import { useUncontrolledDates } from "./auth-service-tools617.js";
import { useDatesContext } from "./auth-service-tools577.js";
import { assignTime } from "./auth-service-tools573.js";
import { Calendar } from "./auth-service-tools616.js";
import { pickCalendarProps } from "./auth-service-tools619.js";
import { HiddenDatesInput } from "./auth-service-tools578.js";
import { dateStringParser } from "./auth-service-tools621.js";
import { isDateValid } from "./auth-service-tools622.js";
import { factory } from "./auth-service-tools248.js";
import { useInputProps } from "./auth-service-tools308.js";
import { CloseButton } from "./auth-service-tools280.js";
import { useDidUpdate } from "./auth-service-tools369.js";
import { Input } from "./auth-service-tools299.js";
import { Popover } from "./auth-service-tools264.js";
const defaultProps = {
  valueFormat: "MMMM D, YYYY",
  fixOnBlur: true,
  preserveTime: true
};
const DateInput = factory((_props, ref) => {
  const props = useInputProps("DateInput", defaultProps, _props);
  const {
    inputProps,
    wrapperProps,
    value,
    defaultValue,
    onChange,
    clearable,
    clearButtonProps,
    popoverProps,
    getDayProps,
    locale,
    valueFormat,
    dateParser,
    minDate,
    maxDate,
    fixOnBlur,
    onFocus,
    onBlur,
    onClick,
    readOnly,
    name,
    form,
    rightSection,
    unstyled,
    classNames,
    styles,
    allowDeselect,
    preserveTime,
    date,
    defaultDate,
    onDateChange,
    ...rest
  } = props;
  const [dropdownOpened, setDropdownOpened] = useState(false);
  const {
    calendarProps,
    others
  } = pickCalendarProps(rest);
  const ctx = useDatesContext();

  const defaultDateParser = val => {
    const parsedDate = dayjsImport(val, valueFormat, ctx.getLocale(locale)).toDate();
    return Number.isNaN(parsedDate.getTime()) ? dateStringParser(val, ctx.getTimezone()) : parsedDate;
  };

  const _dateParser = dateParser || defaultDateParser;

  const _allowDeselect = allowDeselect !== void 0 ? allowDeselect : clearable;

  const formatValue = val => val ? dayjsImport(val).locale(ctx.getLocale(locale)).format(valueFormat) : "";

  const [_value, setValue, controlled] = useUncontrolledDates({
    type: "default",
    value,
    defaultValue,
    onChange
  });
  const [_date, setDate] = useUncontrolledDates({
    type: "default",
    value: date,
    defaultValue: defaultValue || defaultDate,
    onChange: onDateChange
  });
  useEffect(() => {
    if (controlled) {
      setDate(value);
    }
  }, [controlled, value]);
  const [inputValue, setInputValue] = useState(formatValue(_value));
  useEffect(() => {
    setInputValue(formatValue(_value));
  }, [ctx.getLocale(locale)]);

  const handleInputChange = event => {
    const val = event.currentTarget.value;
    setInputValue(val);
    setDropdownOpened(true);

    if (val.trim() === "" && clearable) {
      setValue(null);
    } else {
      const dateValue = _dateParser(val);

      if (isDateValid({
        date: dateValue,
        minDate,
        maxDate
      })) {
        setValue(dateValue);
        setDate(dateValue);
      }
    }
  };

  const handleInputBlur = event => {
    onBlur == null ? void 0 : onBlur(event);
    setDropdownOpened(false);
    fixOnBlur && setInputValue(formatValue(_value));
  };

  const handleInputFocus = event => {
    onFocus == null ? void 0 : onFocus(event);
    setDropdownOpened(true);
  };

  const handleInputClick = event => {
    onClick == null ? void 0 : onClick(event);
    setDropdownOpened(true);
  };

  const _getDayProps = day => ({ ...(getDayProps == null ? void 0 : getDayProps(day)),
    selected: dayjsImport(_value).isSame(day, "day"),
    onClick: event => {
      var _a, _b;

      (_b = getDayProps == null ? void 0 : (_a = getDayProps(day)).onClick) == null ? void 0 : _b.call(_a, event);
      const valueWithTime = preserveTime ? assignTime(_value, day) : day;
      const val = clearable && _allowDeselect ? dayjsImport(_value).isSame(day, "day") ? null : valueWithTime : valueWithTime;
      setValue(val);
      !controlled && setInputValue(formatValue(val));
      setDropdownOpened(false);
    }
  });

  const _rightSection = rightSection || (clearable && _value && !readOnly ? /* @__PURE__ */jsxRuntimeExports.jsx(CloseButton, {
    variant: "transparent",
    onMouseDown: event => event.preventDefault(),
    tabIndex: -1,
    onClick: () => {
      setValue(null);
      !controlled && setInputValue("");
      setDropdownOpened(false);
    },
    unstyled,
    size: inputProps.size || "sm",
    ...clearButtonProps
  }) : null);

  useDidUpdate(() => {
    _value !== void 0 && !dropdownOpened && setInputValue(formatValue(_value));
  }, [_value]);
  return /* @__PURE__ */jsxRuntimeExports.jsxs(jsxRuntimeExports.Fragment, {
    children: [/* @__PURE__ */jsxRuntimeExports.jsx(Input.Wrapper, { ...wrapperProps,
      __staticSelector: "DateInput",
      children: /* @__PURE__ */jsxRuntimeExports.jsxs(Popover, {
        opened: dropdownOpened,
        trapFocus: false,
        position: "bottom-start",
        disabled: readOnly,
        withRoles: false,
        unstyled,
        ...popoverProps,
        children: [/* @__PURE__ */jsxRuntimeExports.jsx(Popover.Target, {
          children: /* @__PURE__ */jsxRuntimeExports.jsx(Input, {
            "data-dates-input": true,
            "data-read-only": readOnly || void 0,
            autoComplete: "off",
            ref,
            value: inputValue,
            onChange: handleInputChange,
            onBlur: handleInputBlur,
            onFocus: handleInputFocus,
            onClick: handleInputClick,
            readOnly,
            rightSection: _rightSection,
            ...inputProps,
            ...others,
            __staticSelector: "DateInput"
          })
        }), /* @__PURE__ */jsxRuntimeExports.jsx(Popover.Dropdown, {
          onMouseDown: event => event.preventDefault(),
          "data-dates-dropdown": true,
          children: /* @__PURE__ */jsxRuntimeExports.jsx(Calendar, {
            __staticSelector: "DateInput",
            __timezoneApplied: true,
            ...calendarProps,
            classNames,
            styles,
            unstyled,
            __preventFocus: true,
            minDate,
            maxDate,
            locale,
            getDayProps: _getDayProps,
            size: inputProps.size,
            date: _date,
            onDateChange: setDate
          })
        })]
      })
    }), /* @__PURE__ */jsxRuntimeExports.jsx(HiddenDatesInput, {
      name,
      form,
      value: _value,
      type: "default"
    })]
  });
});
DateInput.classes = { ...Input.classes,
  ...Calendar.classes
};
DateInput.displayName = "@mantine/dates/DateInput";
export { DateInput };